import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut as firebaseSignOut, 
  sendPasswordResetEmail,
  onAuthStateChanged,
  User as FirebaseUser
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { User, UserRole } from '../types';

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userData: User | null;
  loading: boolean;
  error: string | null;
  signUp: (email: string, password: string, name: string, role: UserRole, phoneNumber?: string) => Promise<void>;
  signIn: (phoneNumber: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateUserProfile: (data: Partial<User>) => Promise<void>;
  refreshUserData: () => Promise<void>;
  photoURL?: string;
  setPhotoURL?: (url: string) => void;
  setUserData: (userData: User | null) => void;
  logout?: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [photoURL, setPhotoURL] = useState<string | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setLoading(true);
      
      if (user) {
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data() as User;
            setUserData(userData);
            // تحديث صورة الملف الشخصي من بيانات المستخدم
            if (userData.profilePicture) {
              setPhotoURL(userData.profilePicture);
            }
            localStorage.setItem('userRole', userData.role);
          } else {
            setUserData(null);
          }
        } catch (err) {
          console.error('Error fetching user data:', err);
          setError('Failed to fetch user data');
        }
      } else {
        setUserData(null);
        setPhotoURL(undefined);
        localStorage.removeItem('userRole');
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signUp = async (email: string, password: string, name: string, role: UserRole, phoneNumber?: string) => {
    try {
      setError(null);
      setLoading(true);
      
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Create user document in Firestore
      const userData: User = {
        id: user.uid,
        name,
        email,
        role,
        phoneNumber: phoneNumber || '',
        isVerified: false,
        createdAt: Date.now(),
        updatedAt: Date.now()
      };
      
      await setDoc(doc(db, 'users', user.uid), userData);
      setUserData(userData);
      localStorage.setItem('userRole', userData.role);
      
    } catch (err: any) {
      console.error('Error signing up:', err);
      setError(err.message || 'Failed to sign up');
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (phoneNumber: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      
      // تحويل رقم الهاتف إلى بريد إلكتروني
      const email = `${phoneNumber}@example.com`;
      
      await signInWithEmailAndPassword(auth, email, password);
      
      // جلب بيانات المستخدم من Firestore وتخزين دوره
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser!.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data() as User;
        setUserData(userData);
        localStorage.setItem('userRole', userData.role);
        // إضافة token في localStorage لتسهيل التحقق من حالة تسجيل الدخول
        localStorage.setItem('token', 'logged-in');
      }
      
    } catch (err: any) {
      console.error('Error signing in:', err);
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      await firebaseSignOut(auth);
      localStorage.removeItem('userRole');
      localStorage.removeItem('token');
    } catch (err: any) {
      console.error('Error signing out:', err);
      setError(err.message || 'Failed to sign out');
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setError(null);
      setLoading(true);
      
      await sendPasswordResetEmail(auth, email);
      
    } catch (err: any) {
      console.error('Error resetting password:', err);
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const updateUserProfile = async (data: Partial<User>) => {
    try {
      setError(null);
      setLoading(true);
      
      if (!currentUser) {
        throw new Error('No user is signed in');
      }
      
      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, {
        ...data,
        updatedAt: Date.now()
      });
      
      // Update local state
      if (userData) {
        setUserData({
          ...userData,
          ...data,
          updatedAt: Date.now()
        });
      }
      
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const refreshUserData = async () => {
    try {
      if (!currentUser) return;
      
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data() as User;
        setUserData(userData);
        // تحديث صورة الملف الشخصي من بيانات المستخدم
        if (userData.profilePicture) {
          setPhotoURL(userData.profilePicture);
        }
      }
    } catch (err: any) {
      console.error('Error refreshing user data:', err);
      setError(err.message || 'Failed to refresh user data');
    }
  };

  const logout = async () => {
    try {
      await signOut();
    } catch (err: any) {
      console.error('Error logging out:', err);
      setError(err.message || 'Failed to log out');
    }
  };

  const value = {
    currentUser,
    userData,
    loading,
    error,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updateUserProfile,
    refreshUserData,
    setUserData,
    logout,
    photoURL,
    setPhotoURL
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
